(function () {
  'use strict';

  // ─── IA Persistent Cart v2.0 ───
  // Fixed: race condition where removed items reappear from cloud.
  // Key changes:
  //   1. Immediate cloud save on all cart mutations (no debounce)
  //   2. Cloud restore only happens ONCE per session (first page load, empty local cart)
  //   3. Session flag prevents any further restores after a local change
  //   4. sendBeacon on beforeunload as safety net
  //   5. Local changes ALWAYS win over cloud within the same session

  var customer = window.__IA_CUSTOMER;
  if (!customer || !customer.id) {
    console.log('[IA Cart] No customer logged in, persistent cart inactive');
    return;
  }

  var CUSTOMER_ID = String(customer.id);
  var POLL_INTERVAL = 30000;
  var SESSION_KEY = 'ia_cart_session_active';
  var CART_STATE_KEY = 'ia_last_cart_state';
  var LOCAL_CHANGED_KEY = 'ia_cart_local_changed';
  var HAD_ITEMS_KEY = 'ia_cart_had_items'; // localStorage — persists across sessions

  // Anti-loop guard — true while we're restoring from cloud
  var isSyncing = false;
  // Track if we've already attempted a restore this session
  var restoreAttempted = false;

  function now() { return Date.now(); }

  function hashItems(items) {
    if (!items || items.length === 0) return 'empty';
    return items
      .map(function (i) { return String(i.variant_id) + ':' + String(i.quantity); })
      .sort()
      .join('|');
  }

  // ─── Session flags ───
  // "local changed" = user made a cart change this session → never restore from cloud
  function markLocalChanged() {
    try { sessionStorage.setItem(LOCAL_CHANGED_KEY, '1'); } catch (e) {}
  }

  function hasLocalChanged() {
    try { return sessionStorage.getItem(LOCAL_CHANGED_KEY) === '1'; } catch (e) { return false; }
  }

  // "session active" = we've already loaded and attempted restore
  function markSessionActive() {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
  }

  function isSessionActive() {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) { return false; }
  }

  // ─── Cross-session "had items" tracking (localStorage) ───
  // When a customer has items in cart and leaves the site (for checkout or otherwise),
  // this flag persists. On return with empty cart → likely completed an order → don't restore.
  function markHadItems() {
    try { localStorage.setItem(HAD_ITEMS_KEY, String(Date.now())); } catch (e) {}
  }

  function clearHadItems() {
    try { localStorage.removeItem(HAD_ITEMS_KEY); } catch (e) {}
  }

  function hadItemsBefore() {
    try { return !!localStorage.getItem(HAD_ITEMS_KEY); } catch (e) { return false; }
  }

  // ─── Cart fetcher (uses shared helper to avoid recursion) ───
  function getCart() {
    if (window.__IA_getCart) return window.__IA_getCart();
    return fetch('/cart.json').then(function (r) { return r.json(); });
  }

  // ─── Supabase helpers ───
  function supaPost(endpoint, payload, headers) {
    if (!window.__IA_supabasePost) {
      console.warn('[IA Cart] Missing __IA_supabasePost');
      return Promise.resolve(null);
    }
    return window.__IA_supabasePost(endpoint, payload, headers);
  }

  function supaGet(endpoint) {
    if (!window.__IA_supabaseGet) {
      console.warn('[IA Cart] Missing __IA_supabaseGet');
      return Promise.resolve(null);
    }
    return window.__IA_supabaseGet(endpoint);
  }

  // ─── Cloud save (immediate, no debounce) ───
  function buildCartData(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return { items: [], item_count: 0, updated_at: new Date().toISOString() };
    }
    return {
      items: cart.items.map(function (i) {
        return {
          variant_id: i.variant_id,
          quantity: i.quantity,
          product_title: i.product_title || '',
          variant_title: i.variant_title || '',
          price: i.price || 0,
          line_price: i.line_price || (i.price * i.quantity) || 0,
          image: i.image || (i.featured_image ? i.featured_image.url : ''),
          sku: i.sku || '',
          handle: i.handle || '',
          url: i.url || ''
        };
      }),
      item_count: cart.item_count,
      total_price: cart.total_price || 0,
      updated_at: new Date().toISOString()
    };
  }

  function saveCartToCloud(cart) {
    var cartData = buildCartData(cart);
    console.log('[IA Cart] Saving to cloud:', cartData.items.length, 'items');

    // Track if customer has items (persists across sessions via localStorage)
    if (cartData.items.length > 0) {
      markHadItems();
    } else {
      // Cart is now empty — clear the flag (intentional clear or order completed)
      clearHadItems();
    }

    // Stash in sessionStorage for beacon fallback
    try { sessionStorage.setItem(CART_STATE_KEY, JSON.stringify(cartData)); } catch (e) {}

    return supaPost(
      '/rest/v1/persistent_carts',
      { customer_id: CUSTOMER_ID, cart_data: cartData },
      { Prefer: 'resolution=merge-duplicates' }
    );
  }

  function loadCloud() {
    return supaGet(
      '/rest/v1/persistent_carts?customer_id=eq.' + CUSTOMER_ID + '&select=cart_data'
    ).then(function (r) {
      return r && r[0] ? r[0].cart_data : null;
    });
  }

  // ─── Restore from cloud (ONE TIME only, first page load) ───
  async function tryRestoreFromCloud() {
    // Never restore if local changes happened this session
    if (hasLocalChanged()) {
      console.log('[IA Cart] Restore skipped — local changes this session');
      return;
    }

    // Never restore if we already attempted this session
    if (restoreAttempted) {
      console.log('[IA Cart] Restore skipped — already attempted this session');
      return;
    }

    restoreAttempted = true;

    try {
      var cart = await getCart();

      // Only restore when local cart is empty
      if (cart && cart.item_count > 0) {
        console.log('[IA Cart] Local cart has items, no restore needed');
        // Sync local → cloud if cloud is behind
        await saveCartToCloud(cart);
        return;
      }

      var cloud = await loadCloud();
      if (!cloud || !cloud.items || cloud.items.length === 0) {
        console.log('[IA Cart] Cloud cart is also empty, nothing to restore');
        clearHadItems();
        return;
      }

      // Only restore on FIRST session page load (not after navigation within session)
      if (isSessionActive()) {
        console.log('[IA Cart] Session already active, skipping restore (user may have cleared cart)');
        return;
      }

      // KEY FIX: If this browser previously had items and now the cart is empty,
      // it means the customer likely completed an order (checkout clears cart server-side).
      // Don't restore old items — clear cloud instead.
      if (hadItemsBefore()) {
        console.log('[IA Cart] Had items before but cart is now empty — likely completed order. Clearing cloud.');
        clearHadItems();
        await saveCartToCloud({ items: [], item_count: 0 });
        return;
      }

      // STALENESS CHECK: If cloud cart is older than 4 hours, don't restore.
      // Stale carts are almost always from completed orders.
      // Active shoppers update their cart frequently, so 4h is generous.
      if (cloud.updated_at) {
        var cloudAge = Date.now() - new Date(cloud.updated_at).getTime();
        var FOUR_HOURS = 4 * 60 * 60 * 1000;
        if (cloudAge > FOUR_HOURS) {
          console.log('[IA Cart] Cloud cart is ' + Math.round(cloudAge / 3600000) + 'h old — too stale to restore. Clearing.');
          await saveCartToCloud({ items: [], item_count: 0 });
          return;
        }
      }

      // Actually restore (only for cross-device with fresh cloud data)
      isSyncing = true;
      console.log('[IA Cart] Restoring', cloud.items.length, 'items from cloud');

      await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: cloud.items.map(function (i) {
            return { id: i.variant_id, quantity: i.quantity };
          })
        })
      });

      isSyncing = false;
      console.log('[IA Cart] Restore complete');
    } catch (e) {
      isSyncing = false;
      console.warn('[IA Cart] Restore failed:', e);
    }
  }

  // ─── Handle cart mutation (immediate save) ───
  function onCartMutation() {
    if (isSyncing) return;

    markLocalChanged();

    // Immediate save — no debounce
    getCart().then(function (cart) {
      return saveCartToCloud(cart);
    }).catch(function (e) {
      console.warn('[IA Cart] Save error:', e);
    });
  }

  // ─── Poll: only pushes local→cloud, never restores ───
  async function pollCloud() {
    if (isSyncing) return;

    // After session is active, polling only syncs local→cloud (never cloud→local)
    try {
      var cart = await getCart();
      var cloud = await loadCloud();

      var localHash = hashItems(cart && cart.items ? cart.items : []);
      var cloudHash = hashItems(cloud && cloud.items ? cloud.items : []);

      // If they differ and local has items, push local to cloud
      if (localHash !== cloudHash && localHash !== 'empty') {
        console.log('[IA Cart] Poll: syncing local→cloud (local wins)');
        await saveCartToCloud(cart);
      }

      // If local is empty and cloud has items, only clear cloud if local changed this session
      if (localHash === 'empty' && cloudHash !== 'empty' && hasLocalChanged()) {
        console.log('[IA Cart] Poll: local empty + local changed → clearing cloud');
        await saveCartToCloud(cart);
      }
    } catch (e) {
      console.warn('[IA Cart] Poll error:', e);
    }
  }

  // ─── Intercept fetch for cart mutations ───
  (function interceptCartFetch() {
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var opts = arguments[1] || {};
      var method = (opts.method || 'GET').toUpperCase();
      var isCartMutation = typeof url === 'string' &&
        (url.indexOf('/cart/change') !== -1 ||
         url.indexOf('/cart/update') !== -1 ||
         url.indexOf('/cart/clear') !== -1 ||
         url.indexOf('/cart/add') !== -1) &&
        method === 'POST';

      if (isCartMutation && !isSyncing) {
        return origFetch.apply(this, arguments).then(function (response) {
          // Clone response to read cart state
          var cloned = response.clone();
          cloned.json().then(function (cart) {
            markLocalChanged();
            var cartData = buildCartData(cart);
            try { sessionStorage.setItem(CART_STATE_KEY, JSON.stringify(cartData)); } catch (e) {}
            console.log('[IA Cart] Mutation intercepted, saving immediately:', cartData.items.length, 'items');
            // Immediate cloud save
            saveCartToCloud(cart);
          }).catch(function () {
            // If we can't parse response, fetch cart fresh and save
            onCartMutation();
          });
          return response;
        });
      }
      return origFetch.apply(this, arguments);
    };
  })();

  // ─── Also hook into shared cart-change mechanism ───
  if (window.__IA_onCartChange) {
    window.__IA_onCartChange(function () {
      if (!isSyncing) onCartMutation();
    });
  }

  // ─── Form submissions (add to cart forms) ───
  document.addEventListener('submit', function (e) {
    try {
      if (e.target && e.target.action && e.target.action.indexOf('/cart') !== -1) {
        setTimeout(function () {
          onCartMutation();
        }, 500);
      }
    } catch (_) {}
  });

  // ─── beforeunload beacon ───
  window.addEventListener('beforeunload', function () {
    try {
      var SUPA_URL = window.__IA_SUPABASE_URL;
      var SUPA_KEY = window.__IA_SUPABASE_KEY;
      if (!SUPA_URL || !SUPA_KEY) return;

      var cartJson = sessionStorage.getItem(CART_STATE_KEY);
      if (!cartJson) return; // No stashed state, nothing to send

      var payload = JSON.stringify({
        customer_id: CUSTOMER_ID,
        cart_data: JSON.parse(cartJson)
      });
      var blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(
        SUPA_URL + '/rest/v1/persistent_carts?on_conflict=customer_id&apikey=' + SUPA_KEY,
        blob
      );
      console.log('[IA Cart] Beacon sent on unload');
    } catch (e) {}
  });

  // ─── Initialize ───
  // Mark session active, then try one-time restore
  var wasSessionActive = isSessionActive();
  markSessionActive();

  if (!wasSessionActive) {
    // Brand new session (new tab/window) — try restore
    tryRestoreFromCloud();
  } else {
    // Continuing session (navigation within site) — just sync local→cloud
    restoreAttempted = true; // prevent any restore
    getCart().then(function (cart) {
      if (cart && cart.item_count > 0) {
        saveCartToCloud(cart);
      }
    }).catch(function () {});
  }

  // Start polling
  setInterval(pollCloud, POLL_INTERVAL);

  console.log('[IA Cart] Persistent cart v2.0 initialized for customer', CUSTOMER_ID);
})();
